import { useMemo } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { BulkImportRow } from './harborBulkImport.types';

const mapContainerStyle = { width: '100%', height: '360px' };

const defaultCenter = { lat: 60.1699, lng: 24.9384 };

function parseCoord(value: string): number | null {
  const str = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    return null;
  }
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

interface HarborImportMapPreviewProps {
  rows: BulkImportRow[];
}

export function HarborImportMapPreview({ rows }: HarborImportMapPreviewProps) {
  const markers = useMemo(() => {
    return rows
      .map((row, index) => {
        const lat = parseCoord(row.latitude);
        const lng = parseCoord(row.longitude);
        if (lat == null || lng == null) {
          return null;
        }
        return {
          id: row.id,
          lat,
          lng,
          title: row.name.trim() || `Row ${index + 1}`,
          index,
        };
      })
      .filter(Boolean) as {
      id: string;
      lat: number;
      lng: number;
      title: string;
      index: number;
    }[];
  }, [rows]);

  const center = markers[0]
    ? { lat: markers[0].lat, lng: markers[0].lng }
    : defaultCenter;

  return (
    <div className="rounded border border-gray-200 overflow-hidden bg-gray-50" role="region" aria-label="Harbour import coordinate preview map">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={markers.length === 1 ? 12 : 8}
      >
        {markers.map((m) => (
          <Marker
            key={m.id}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.title}
            label={{
              text: String(m.index + 1),
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          />
        ))}
      </GoogleMap>
      {markers.length === 0 && (
        <p className="text-sm text-gray-500 p-3 border-t border-gray-200">
          Add valid latitude and longitude values to see markers on the map.
        </p>
      )}
    </div>
  );
}
